import { useDeviceModeContext } from 'client/context/DeviceModeContext'
import React from 'react'
import { FontUpdate } from './BaseFont'
import TextFont from './TextFont'

interface TextFontUpdaterProps<T> {
  entity: T
  update: (updatedEntity: T) => void
  nestedKey?: keyof T
  fontFamilyKey?: string | keyof (T | T[keyof T])
  fontWeightKey?: string | keyof (T | T[keyof T])
  fontStyleKey?: string | keyof (T | T[keyof T])
  mobileFontFamilyKey?: string | keyof (T | T[keyof T])
  mobileFontWeightKey?: string | keyof (T | T[keyof T])
  mobileFontStyleKey?: string | keyof (T | T[keyof T])
  fontFileIdKey?: string | keyof (T | T[keyof T])
  mobileFontFileIdKey?: string | keyof (T | T[keyof T])
}

function getUpdatedEntity<T>(
  entity: T,
  update: FontUpdate,
  nestedKey: keyof T,
  fontFamilyKey: keyof T,
  fontWeightKey: keyof T,
  fontStyleKey: keyof T,
  fontFileIdKey: keyof T,
) {
  const target = nestedKey
    ? { ...(entity[nestedKey] as any) }
    : ({ ...entity } as T)

  if (update.fontFileId) {
    target[fontFamilyKey] = update.fontFamily
    target[fontFileIdKey] = update.fontFileId
    delete target[fontStyleKey]
    delete target[fontWeightKey]
  } else {
    delete target[fontFileIdKey]
    target[fontFamilyKey] = update.fontFamily
    if (update.fontFamily === 'inherit') {
      delete target[fontStyleKey]
      delete target[fontWeightKey]
    } else {
      if (update.fontStyle) {
        target[fontStyleKey] = update.fontStyle
      }

      if (update.fontWeight) {
        target[fontWeightKey] = update.fontWeight
      }
    }

    if (target[fontFileIdKey]) {
      delete target[fontFileIdKey]
    }
  }

  return nestedKey ? { ...entity, [nestedKey]: target } : { ...target }
}

function TextFontUpdater<T extends Record<string, any>>({
  entity,
  update,
  nestedKey = '',
  fontFamilyKey = 'fontFamily',
  fontWeightKey = 'fontWeight',
  fontStyleKey = 'fontStyle',
  mobileFontFamilyKey = 'mobileFontFamily',
  mobileFontWeightKey = 'mobileFontWeight',
  mobileFontStyleKey = 'mobileFontStyle',
  fontFileIdKey = 'fontFileId',
  mobileFontFileIdKey = 'mobileFontFileId',
}: TextFontUpdaterProps<T>) {
  const { isMobile } = useDeviceModeContext()

  const target = nestedKey ? (entity[nestedKey] as any) : (entity as T)

  const getValue = (mobileKey: keyof T, desktopKey: keyof T) =>
    isMobile ? target[mobileKey] || target[desktopKey] : target[desktopKey]

  const fontFamily = getValue(
    mobileFontFamilyKey as keyof T,
    fontFamilyKey as keyof T,
  )
  const fontWeight = getValue(
    mobileFontWeightKey as keyof T,
    fontWeightKey as keyof T,
  )
  const fontStyle = getValue(
    mobileFontStyleKey as keyof T,
    fontStyleKey as keyof T,
  )

  function handleFontChange(fontUpdate: FontUpdate) {
    const keys = isMobile
      ? {
          fontFamily: mobileFontFamilyKey,
          fontWeight: mobileFontWeightKey,
          fontStyle: mobileFontStyleKey,
          fontFileId: mobileFontFileIdKey,
        }
      : {
          fontFamily: fontFamilyKey,
          fontWeight: fontWeightKey,
          fontStyle: fontStyleKey,
          fontFileId: fontFileIdKey,
        }

    const updatedEntity = getUpdatedEntity(
      entity,
      fontUpdate,
      nestedKey,
      keys.fontFamily,
      keys.fontWeight,
      keys.fontStyle,
      keys.fontFileId,
    )

    update(updatedEntity)
  }

  return (
    <TextFont
      fontFamily={fontFamily}
      fontStyle={fontStyle}
      fontWeight={fontWeight}
      update={handleFontChange}
    />
  )
}
export default TextFontUpdater
